import React from "react";
import { motion } from "framer-motion";
import { FaHandshake, FaRocket, FaLightbulb } from "react-icons/fa";

function AboutUsPage() {
  // Animation Variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const textVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  // Animation variants for individual items (e.g., values)
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

    const imageVariants = {
      hidden: { opacity: 0, x: 50 },
      visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
    };

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 md:px-8 bg-blue-600 text-white relative overflow-hidden">
        <div className="container mx-auto text-center relative z-10">
          <motion.h1
            className="text-4xl md:text-6xl font-bold mb-4 md:mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            About Keziah Global Private Limited
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Your trusted partner for accessible and innovative financial solutions in India.
          </motion.p>
        </div>
      </section>

      {/* Our Mission */}
      <motion.section
        className="py-16 md:py-24 px-4 md:px-8"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container mx-auto">
          <motion.div className="w-full" variants={textVariants}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 text-center">
              Our Mission
            </h2>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed text-center max-w-4xl mx-auto">
              At Keziah Global Private Limited, our mission is to empower individuals and
              communities across India by providing accessible, affordable, and
              secure financial services. We are committed to bridging the gap
              between the unbanked and the formal financial sector, fostering
              economic growth and financial inclusion for all.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Our Values */}
      <motion.section
        className="py-16 md:py-24 px-4 md:px-8 bg-white"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 text-center">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Value 1 */}
            <motion.div className="text-center" variants={itemVariants}>
              <div className="mb-4 text-blue-600">
                <FaHandshake className="text-5xl mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Integrity
              </h3>
              <p className="text-gray-600">
                We uphold the highest ethical standards in all our operations,
                ensuring transparency and trust in every interaction.
              </p>
            </motion.div>
            {/* Value 2 */}
            <motion.div className="text-center" variants={itemVariants}>
              <div className="mb-4 text-blue-600">
                <FaRocket className="text-5xl mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Innovation
              </h3>
              <p className="text-gray-600">
                We embrace technology and innovative solutions to deliver
                seamless and convenient banking experiences to our customers.
              </p>
            </motion.div>
            {/* Value 3 */}
            <motion.div className="text-center" variants={itemVariants}>
              <div className="mb-4 text-blue-600">
                <FaLightbulb className="text-5xl mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Customer Focus
              </h3>
              <p className="text-gray-600">
                Our customers are at the heart of everything we do. We are
                dedicated to understanding their needs and exceeding their
                expectations.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Our Story */}
      <motion.section
        className="py-16 md:py-24 px-4 md:px-8"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container mx-auto">
          <motion.div className="w-full" variants={textVariants}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 text-center">
              Our Story
            </h2>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed text-center max-w-4xl mx-auto">
              Founded in 2025, Keziah Global Private Limited began with a vision to
              transform the financial landscape of rural India. Recognizing the
              challenges faced by underserved communities in accessing formal
              banking services, we set out to create a company that would bridge
              this gap. Today, we are proud to be a leading Business
              Correspondent for Fino Payments Bank, serving thousands of
              customers across the country.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Partnership with Fino Payments Bank */}
      <motion.section
        className="py-16 md:py-24 px-4 md:px-8 bg-white"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            {/* Text Content */}
            <div className="w-full md:w-1/2 text-center md:text-left mb-8 md:mb-0">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-6 leading-tight"
                variants={textVariants}
              >
                Our Partnership with <br className="hidden md:block" />
                <span className="text-blue-600">Fino Payments Bank</span>
              </motion.h2>
              <motion.p
                className="text-base md:text-lg text-gray-600 leading-relaxed"
                variants={textVariants}
              >
                As a Business Correspondent of Fino Payments Bank, we bring you
                the trust and reliability of a leading name in the Indian banking
                sector. This partnership enables us to offer a wider range of
                services and reach more people across the country.
              </motion.p>
            </div>

            {/* Logo */}
            <div className="w-full md:w-1/3 flex justify-center md:justify-end">
              <motion.div
                variants={imageVariants}
                className="w-full max-w-xs md:max-w-sm p-6 md:p-8 bg-white rounded-lg shadow-md"
              >
                <img
                  src="/assets/Fino.png" // Replace with your Fino logo
                  alt="Fino Payments Bank Logo"
                  className="h-16 md:h-20 mx-auto"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
}

export default AboutUsPage;